import React from "react";
import { LegalPage, LegalSection as S, A, Strong } from "@/components/LegalLayout";
import { CONTACT_EMAIL } from "@/config";

const Mail = () => <a href={`mailto:${CONTACT_EMAIL}`} className="text-[#e2ff31] hover:underline">{CONTACT_EMAIL}</a>;

const PROVIDERS = [
  ["Stripe", "Payment processing. Stripe collects your card and billing details directly; we only receive the payment status, amount and your email."],
  ["Resend", "Sends our emails (receipts, download links, early-access and “It's live” alerts)."],
  ["Google Cloud", "Securely stores beats, artwork, license PDFs and any screenshots you send with a problem report."],
  ["Our hosting provider", "Runs the website and the database that stores your account and order records."],
];

export default function PrivacyPolicy() {
  return (
    <LegalPage testid="privacy-page" title="Privacy Policy"
      intro="This policy explains what information Co Mann Beats collects when you use comannbeats.com, why we collect it, and the choices you have. We keep it to what we need to run the store.">
      <S n={1} title="Information we collect">
        <ul className="list-disc pl-5 space-y-1.5">
          <li><Strong>Account details:</Strong> your name, email and a securely hashed password.</li>
          <li><Strong>Orders:</Strong> what you bought, when, the amount, and your Stripe payment reference. We don't receive or store full card numbers.</li>
          <li><Strong>Download and delivery records:</Strong> when and how often your files were downloaded, so we can confirm delivery and help with problems.</li>
          <li><Strong>Messages you send us:</Strong> problem reports (including any screenshots), emails, and the email you enter for “Notify me”.</li>
          <li><Strong>Usage data:</Strong> basic activity such as plays, page visits, wishlist saves, and whether you saved or shared your post-purchase card, used to improve the store.</li>
          <li><Strong>Email engagement:</Strong> our drop and early-access emails contain links and a tiny image that tell us if an email was opened or clicked.</li>
        </ul>
      </S>
      <S n={2} title="How we use it">
        <ul className="list-disc pl-5 space-y-1.5">
          <li>To process purchases, deliver files and licenses, and provide support.</li>
          <li>To send order emails, and drop or early-access emails you've asked for or are eligible for as a past buyer.</li>
          <li>To prevent fraud, chargeback abuse and unauthorized downloads.</li>
          <li>To understand what's working and improve the Site.</li>
        </ul>
        <p>We don't sell your personal information, and we don't use it for third-party advertising.</p>
      </S>
      <S n={3} title="What other visitors can see">
        <p>The live purchase pop-up on the homepage may show a buyer's first name and last initial (e.g. “Marcus J.”) or simply “Someone”, plus the beat title. Your email and full name are never shown publicly. Email us if you'd like your purchases left out of the pop-up.</p>
      </S>
      <S n={4} title="Service providers we share data with">
        <div className="grid gap-2">
          {PROVIDERS.map(([n, d]) => (
            <div key={n} className="rounded-xl border border-[#2a2a2a] bg-[#121212] px-4 py-3"><p className="text-[#f3f4f6] font-medium">{n}</p><p className="text-sm">{d}</p></div>
          ))}
        </div>
        <p>We may also share information if required by law or to protect our rights, for example in a fraud investigation or chargeback dispute.</p>
      </S>
      <S n={5} title="Cookies and local storage">
        <p>We use essential cookies to keep you signed in securely. We also store a few small settings in your browser, such as a hidden pop-up or a “Notify me” you signed up for. We don't use third-party advertising cookies.</p>
      </S>
      <S n={6} title="How long we keep it">
        <p>We keep order and license records for as long as needed to prove your exclusive rights and meet tax and legal requirements. Notify-me emails are used only for that drop. You can ask us to delete your account at any time; we'll keep only what we're legally required to.</p>
      </S>
      <S n={7} title="Your choices and rights">
        <p>You can ask to see, correct or delete your personal information, or to stop receiving drop emails, by emailing <Mail />. Depending on where you live, you may have additional rights under local privacy laws, and we'll honor them.</p>
      </S>
      <S n={8} title="Security">
        <p>The Site uses HTTPS encryption, passwords are hashed, payments run through Stripe, and files are delivered with private links that expire. No system is perfectly secure, but we take reasonable steps to protect your information.</p>
      </S>
      <S n={9} title="Children">
        <p>The Site isn't directed to children under 13, and we don't knowingly collect their information.</p>
      </S>
      <S n={10} title="Changes and contact">
        <p>We'll post any updates here and change the date at the top. Questions? Email <Mail />. Co Mann Beats, Maryland, United States. See also our <A to="/terms">Terms of Service</A> and <A to="/refund-policy">Refund Policy</A>.</p>
      </S>
    </LegalPage>
  );
}
